import DOMPurify from 'dompurify'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded'
import { Box, Card, Divider, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

interface MonthlyTldrCardProps {
  /** Scope title shown next to the badge (e.g. "Games — April 2026"). */
  title: string
  /** Summary body; may contain inline <strong>/<em> markup (sanitised). */
  text: string
}

/**
 * "Monthly Insight" hero card — an LLM-generated narrative summary of what is
 * happening in the selected scope this month. The first thing the user reads on
 * the dashboard. Inline emphasis markup is sanitised before rendering.
 */
export const MonthlyTldrCard: FC<MonthlyTldrCardProps> = ({ title, text }) => {
  const { t } = useTranslation('ua-insights')

  const safeHtml = useMemo(() => DOMPurify.sanitize(text, { ALLOWED_TAGS: ['strong', 'em', 'b', 'i'], ALLOWED_ATTR: [] }), [text])

  if (!text) return null

  return (
    <Card
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
        bgcolor: 'background.paper',
      }}
    >
      {/* Oversized, faded glyph anchors the card as an "AI insight" on purpose. */}
      <Box
        aria-hidden
        sx={{ position: 'absolute', top: -24, right: -16, color: 'primary.main', opacity: 0.08, fontSize: 150, lineHeight: 1, pointerEvents: 'none' }}
      >
        <AutoAwesomeRounded fontSize="inherit" />
      </Box>

      {/* Header row shares the `SectionCard` padding (`px: 2`) so the card lines
          up with the section cards stacked beneath it on the dashboard. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 38,
            height: 38,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <AutoAwesomeRounded sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'primary.main', lineHeight: 1.4 }}>
            {t('showcase_monthly_insight')}
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: 1.3,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography
          component="div"
          sx={{
            position: 'relative',
            fontSize: '0.875rem',
            lineHeight: 1.75,
            color: 'text.secondary',
            '& strong, & b': { color: 'text.primary', fontWeight: 700 },
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </Box>
    </Card>
  )
}
